import React from 'react';
import useMediaQuery from "@mui/system/useMediaQuery";
import {Typography, Grid, useTheme} from "@mui/joy";
import Dialog from "@mui/material/Dialog";
import {BasicModalContent} from "@dreebit-registry/vsm-microservice-components";
import {resolveErrorMessage} from "../../utils/resolveErrorMessage";
import {useTranslation} from "react-i18next";
import {useMutation, useApolloClient, useQuery} from "@apollo/client";
import _ from "lodash";
import {Box, Button, CircularProgress} from '@mui/material';
import AssetNameplate from "../AssetNameplate";
import InstalledBaseTDAForm from "../InstalledBaseTDAForm";
import {toast} from "react-toastify";
import {AssetLocationImportTDAs} from "../../graphql/queries/assetLocationImportTDAs";
import {ApproveAssetLocationImport} from "../../graphql/mutations/approveAssetLocationImport";
import {FormikHelpersRef} from "../../types";

interface Props {
    assetLocationId: string;
    open: boolean;
    allAssetLocationIds: string[];
    onClose?: () => void;
    onAssetChange?: (nextId: string) => void;
}

const ImportAssetApprovalModal = ({ assetLocationId, onClose, onAssetChange, open, allAssetLocationIds }: Props) => {
    const theme = useTheme();
    const isFullScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const { t } = useTranslation();
    const apolloClient = useApolloClient();

    const formikRef = React.useRef<FormikHelpersRef | null>(null);
    const [loadingNext, setLoadingNext] = React.useState(false);
    const [loadingApprove, setLoadingApprove] = React.useState(false);

    const tdaQuery = useQuery(AssetLocationImportTDAs, {
        variables: { id: assetLocationId },
    });

    const [approveMutation] = useMutation(ApproveAssetLocationImport);

    const attributes = _.get(tdaQuery, 'data.assetLocation.asset.assetLocationImport.typeDefinitionAttributeList.typeDefinitionAttributes', []);
    const installedBaseNodeTitle = _.get(tdaQuery, 'data.assetLocation.installedBaseNode.title');
    const assetLocationImportId = _.get(tdaQuery, 'data.assetLocation.asset.assetLocationImport.id');

    // Check if we're in single asset mode (from dashboard) or multi-asset mode (from import list)
    const isSingleAssetMode = allAssetLocationIds.length === 1;

    const handleSubmit = async (openNext: boolean) => {
        if (!formikRef.current) return;

        const setLoading = openNext ? setLoadingNext : setLoadingApprove;
        setLoading(true);

        try {
            const validationErrors = await formikRef.current.validate();
            if (Object.keys(validationErrors).length > 0) {
                toast.error(t("Please correct validation errors before submitting."));
                setLoading(false);
                return;
            }

            const values = formikRef.current.getValues();

            const res = await approveMutation({
                variables: { id: assetLocationImportId, input: values }
            });

            if (!res.data?.approveAssetLocationImport?.id) {
                throw new Error("Mutation returned no ID");
            }

            toast.success(t("Imported asset has been approved."));

            // Backend braucht Pause -> 1 Sekunde warten
            setTimeout(async () => {
                // Refetch NUR Asset-Liste
                await apolloClient.refetchQueries({
                    include: ["SearchImportAssets", "AssetLocationImportWidget"],
                });

                if (openNext) {
                    const currentIndex = allAssetLocationIds.indexOf(assetLocationId);
                    const nextId = allAssetLocationIds[currentIndex + 1];
                    if (nextId) {
                        onAssetChange?.(nextId);
                    } else {
                        toast.info(t("No more assets to approve."));
                        onClose?.();
                    }
                } else {
                    onClose?.();
                }

                setLoading(false);
            }, 600);

        } catch (err: any) {
            toast.error(resolveErrorMessage(err, t));
            setLoading(false);
        }
    };


    return (
        <Dialog open={open} onClose={onClose} fullScreen={isFullScreen}>
            <BasicModalContent
                title={
                    <Typography level={'titleSmallLight'}>
                        {t('Approve installation')}:{" "}
                        <Typography level={'titleSmall'}>
                            {installedBaseNodeTitle}
                        </Typography>
                    </Typography>
                }
                onClose={onClose}
            >
                <Box
                    sx={{
                        width: "100%",
                        padding: "13px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        marginTop: "5px",
                    }}
                >
                    <AssetNameplate assetLocationId={assetLocationId} compact={true} disableNavigation showLocation />
                </Box>

                <InstalledBaseTDAForm
                    formStyle={{ paddingTop: '0px' }}
                    attributes={attributes}
                    setFormikHelpersRef={(ref) => (formikRef.current = ref)}
                />

                <Grid container spacing={1}>
                    <Grid xs={isSingleAssetMode ? 6 : 4}>
                        <Button variant="outlined" sx={{ width: "100%" }} onClick={onClose}>
                            {t("Cancel")}
                        </Button>
                    </Grid>
                    {!isSingleAssetMode && (
                        <Grid xs={4}>
                            <Button
                                variant="contained"
                                color="secondary"
                                sx={{ width: "100%" }}
                                onClick={() => handleSubmit(true)}
                                disabled={loadingNext || loadingApprove}
                                startIcon={
                                    loadingNext ? <CircularProgress size={18} color="inherit" /> : null
                                }
                            >
                                {t("Approve & Next")}
                            </Button>
                        </Grid>
                    )}
                    <Grid xs={isSingleAssetMode ? 6 : 4}>
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{ width: "100%" }}
                            onClick={() => handleSubmit(false)}
                            disabled={loadingNext || loadingApprove}
                            startIcon={
                                loadingApprove ? <CircularProgress size={18} color="inherit" /> : null
                            }
                        >
                            {t("Approve")}
                        </Button>
                    </Grid>
                </Grid>
            </BasicModalContent>
        </Dialog>
    );
};

export default ImportAssetApprovalModal;
